/* @flow */
import * as React from "react";
import PropTypes from "prop-types";
import prefixStyles from "nx-components/utils/prefixStyles";
import type { StylesObject } from "nx-components/utils/prefixStyles";
import Theme from "nx-components/Theme";
import ErrorHandler from "nexia_mobile/components/ErrorHandler";
import ErrorPage from "nexia_mobile/components/Pages/ErrorPage";

const pageStyles = prefixStyles({
  wrapper: {
    position: "absolute",
    bottom: 0,
    left: Theme.Padding.SAFE_AREA_LEFT,
    right: Theme.Padding.SAFE_AREA_RIGHT,
    top: 0,
    display: "flex",
    flexDirection: "column",
  },
});

type Props = {
  name: string,
  children: React.Node,
  backgroundColor?: string,
  backgroundImageStyles?: ?StylesObject,
  arbitraryHeight?: boolean,
  // arbitraryHeight works around an iOS bug where two nested containers can
  // scroll. When the keyboard is visible, iOS does NOT shrink the viewport,
  // but instead allows the body to scroll. If a child is also scrollable, the
  // user's scroll attempts will choose seemingly at random between scrolling
  // body and the child.
  // To improve UX during account creation, we remove scrolling from the child
  // by allowing Page to have arbitrary height. The scrolling will now ALWAYS
  // target the body, which causes the header to scroll out of view. For sign
  // up views, this isn't a problem and since it can be easily scrolled back.
  ignoreSafeArea?: boolean,
  fullyLoaded: boolean,
  // fullyLoaded controls a data-attribute for end-to-end testing. For instance,
  // we set it to false on the automations and dashboard page until we have
  // parsed the first set of devices and automations for the location.
};

export default class Page extends React.Component<Props> {
  static contextTypes = {
    // This is just undefined if Page is not mounted inside an AppContainerBreakpointProvider.
    // NOTE: React 16.3 added a new context API which might be less forgiving than this.
    isLargeAppLayout: PropTypes.bool,
  };

  UNSAFE_componentWillMount() {
    if (this.props.arbitraryHeight) {
      document.documentElement.style.position = "relative";
      document.body.style.position = "relative";
    } else {
      document.documentElement.style.position = "fixed";
      document.body.style.position = "fixed";
    }
  }

  render() {
    const {
      name,
      children,
      backgroundColor,
      arbitraryHeight,
      backgroundImageStyles,
      ignoreSafeArea,
      fullyLoaded,
    } = this.props;

    return (
      <ErrorHandler fallback={<ErrorPage />}>
        <main
          data-component-name="Page"
          data-component-prop-name={name}
          data-page-fully-loaded={fullyLoaded}
          style={{
            ...pageStyles.wrapper,
            ...(this.context && this.context.isLargeAppLayout
              ? { left: 0 }
              : null),
            ...(backgroundColor
              ? {
                  backgroundColor,
                }
              : null),
            ...(backgroundImageStyles || null),
            ...(ignoreSafeArea ? { left: 0, right: 0 } : null),
            bottom: arbitraryHeight ? "" : 0,
          }}
        >
          {children}
        </main>
      </ErrorHandler>
    );
  }
}

// Page renders a vertical flex layout, which means any immediate
// children are subject to vertical growing and shrinking unless wrapped
// with a flex: "none" element. Use StaticSizedArea as an easy shortcut
// like the following:
//
// <Page>
//   <StaticSizedArea>
//     {/* Your fixed-height stuff goes here */}
//   </StaticSizedArea>
// </Page>

const staticSizedAreaStyles = prefixStyles({
  container: {
    flex: "none", // keep Page from shrinking us vertically
  },
});

export const StaticSizedArea = ({ children }: { children: React.Node }) => (
  <div style={staticSizedAreaStyles.container}>{children}</div>
);

StaticSizedArea.propTypes = {
  children: PropTypes.node,
};



// WEBPACK FOOTER //
// ./src/components/Page.jsx